import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import NumberRating from 'components/Common/NumberRating'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import SearchTileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/SearchTileCancellationPolicy'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { queryKeySelectedOfferIds } from 'constants/url'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import GeoContext from 'contexts/geoContext'
import getUrgencyLabels from 'lib/getUrgencyLabels'
import { showUserReviewsRating } from 'lib/reviewUtils'
import { setSearchParamValue } from 'lib/urlUtils'
import React, { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import OfferTileLocationSection from '../OfferTileLocationSection'
import OfferTilePropertyHeading from '../OfferTilePropertyHeading'
import SearchRentalAmenities from './SearchRentalAmenities'
import { take } from 'lib/arrayUtils'

interface MappedStateProps {
  windowSearch: string;
  shouldShowMapButton: boolean;
}

interface Props {
  location: string;
  cancellationPolicyType: string;
  offer: App.Offer;
  filters: App.OfferListFilters | undefined;
}

function RentalPropertyDetails(props: Props & MappedStateProps) {
  const {
    location,
    cancellationPolicyType,
    offer,
    windowSearch,
    shouldShowMapButton,
    filters,
  } = props

  const {
    property,
    name,
    id,
  } = offer

  const { currentRegionCode } = useContext(GeoContext)
  const { t } = useTranslation('common')

  const { searchTargetLabel, offerDistanceFromSearchTarget, searchType } =
    useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[id]
  const showSearchDistance =
    searchType === 'landmark' &&
    !!searchTargetLabel &&
    typeof searchTargetDistance === 'number'

  const urgencyLabels = useMemo(() => getUrgencyLabels(offer, currentRegionCode), [offer, currentRegionCode])

  // Rentals don't have inclusions but have amenities. Rentals only have one room type so the following maps the amenities.
  const numberOfAmenitiesToShow = useMemo(() =>
    10 - (2 * [property?.reviews && property.reviews.length > 0, urgencyLabels.length > 0].filter(Boolean).length)
  , [property, urgencyLabels])
  const allAmenities: Array<{icon: string, text: string}> | undefined = useMemo(() => offer.packages[0].roomType?.amenities.map((val) => {
    return {
      text: val.name,
      icon: val.name.toLowerCase().split(' ').join('_'),
    }
  }), [offer])
  const amenities = useMemo(() => allAmenities ? take(allAmenities, numberOfAmenitiesToShow) : [], [allAmenities, numberOfAmenitiesToShow])

  const mapSearchUrl = useMemo(() => `/search/map?${setSearchParamValue(
    windowSearch,
    queryKeySelectedOfferIds,
    encodeOfferIds([offer]),
  )}`, [windowSearch, offer])

  const capacity = useMemo(() => offer.packages[0].roomRate?.capacities.reduce((prev, curr) => {
    const currCapacity = curr.adults + curr.children + curr.infants
    return currCapacity > prev ? currCapacity : prev
  }, 0), [offer])
  const capacityTitle = `Up to ${capacity} guests`

  const shouldShowRating = useMemo(() => property ? showUserReviewsRating(property.rating, property.reviewsTotal) : false, [property])

  return (
    <Group direction="vertical" gap={8}>
      <Group direction="vertical" fullWidth gap={4}>
        {property && shouldShowRating && (
          <CSSBreakpoint min="md">
            <Group direction="horizontal" horizontalAlign="space-between">
              <NumberRating
                variant="medium"
                rating={property.rating}
                total={property.reviewsTotal}
                ratingSubtitle={property.reviewsSource === 'google' ? t('From online reviews', { ns: 'common' }) : undefined}
              />
            </Group>
          </CSSBreakpoint>
        )}
        <OfferUrgencyLabels offer={offer} urgencyLabels={urgencyLabels}/>
        <OfferTileLocationSection
          location={location}
          showSearchDistance={showSearchDistance}
          shouldShowMapLink={shouldShowMapButton}
          mapSearchUrl={mapSearchUrl}
          searchTargetDistance={searchTargetDistance}
          searchTargetLabel={searchTargetLabel}
        />
        {property && (
          <OfferTilePropertyHeading
            name={property.name}
            descriptiveName={name}
          />
        )}
        <BodyText variant="medium">{capacityTitle}</BodyText>
      </Group>
      {property && shouldShowRating && (
        <CSSBreakpoint max="sm">
          <NumberRating
            inlineLabel
            variant="xs"
            rating={property.rating}
            total={property.reviewsTotal}
            ratingSubtitle={
              property.reviewsSource === 'google' ?
                t('From online reviews', { ns: 'common' }) :
                undefined
            }
          />
        </CSSBreakpoint>
      )}
      <SearchTileCancellationPolicy
        cancellationPolicyType={cancellationPolicyType}
        checkInDate={filters?.checkIn}
        timezoneOffset={offer?.property?.timezoneOffset}
      />
      {!!amenities &&
        <Group direction="vertical" gap={16}>
          <SearchRentalAmenities amenities={amenities} />
          {amenities.length > 0 &&
            <BodyText variant="medium" weight="bold" colour="primary" underline>{`View all ${amenities.length} amenities`}</BodyText>
          }
        </Group>
      }
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (state): MappedStateProps => ({
    shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
    windowSearch: state.router.location.search,
  }),
)(RentalPropertyDetails)
